import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { AppState } from 'reducers/reducers';
import { trackEvent } from 'utils/analytics';
import { revenueRentalType, RevenueRentalType } from 'types/TransactionReport';
import useFilters from 'hooks/useFilters';
import { selectedSpotInfo } from 'selectors';
import Loader from 'components/Loader/Loader';
import MonthlyAndDailyRevenue from 'components/RevenueWidgets/MonthlyAndDailyRevenue/MonthlyAndDailyRevenue';
import { OnOffOption, onOffValidation, Period, RevenueType, Timeframe } from 'components/RevenueWidgets/helpers';
import RevenueByRentalType from 'components/RevenueWidgets/RevenueByRentalType';
import YoYRevenueWidget from 'components/RevenueWidgets/YoYRevenueWidget';
import Pacing from 'components/RevenueWidgets/PacingWidget';
import DownloadReport from 'components/RevenueWidgets/DownloadReport/DownloadReport';
import DashboardContent from 'components/Dashboard/DashboardContent/DashboardContent';
import { RevenueProvider } from 'contexts/RevenueContext';
import styles from './RevenueContainer.module.css';

type RevenueContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

function RevenueContainer({ spot, loadingSpots, push }: RevenueContainerProps): JSX.Element {
    const { filters, urlUpdater } = useFilters<{
        revenueType: RevenueType;
        timeframe: Timeframe;
        dailyPeriod: Period;
        rentalType: RevenueRentalType;
        yoyRevenueType: RevenueType;
        month: string;
        momYearCompare: OnOffOption;
        momGuaranteeCompare: OnOffOption;
    }>({
        filterValidators: {
            revenueType(current) {
                return current && Object.values(RevenueType).includes(current as RevenueType)
                    ? current
                    : RevenueType.GROSS_REVENUE;
            },
            timeframe(current) {
                return current && Object.values(Timeframe).includes(current as Timeframe) ? current : Timeframe.MONTHLY;
            },
            momYearCompare: onOffValidation,
            momGuaranteeCompare(current, allParams) {
                return allParams.get('momYearCompare') === 'on' ? 'off' : onOffValidation(current);
            },
            dailyPeriod(current) {
                return current && Object.values(Period).includes(current as Period) ? current : Period.THIRTY;
            },
            rentalType(current) {
                if (!current) return '';
                const rentalTypes = current.split(',').map((rt) => {
                    return Object.values(revenueRentalType).includes(rt as RevenueRentalType) ? rt : null;
                });
                return rentalTypes.filter((rt) => rt !== null).join(',');
            },
            yoyRevenueType(current) {
                return current && Object.values(RevenueType).includes(current as RevenueType)
                    ? current
                    : RevenueType.GROSS_REVENUE;
            },
            month(current) {
                const month = Number(current);
                if (current && month && month > 0 && month <= 12) return current;
                // Since js months start at 0 new Date().getMonth() will return the previous month
                // and that is the expected behavior
                const currentMonth = new Date().getMonth();
                // Exception: when current month is 0 (Jan), we need to return 12 (December)
                return String(currentMonth || 12);
            },
        },
        push,
    });

    useEffect(() => {
        if (spot) {
            trackEvent('Viewed Revenue Page', { spot: spot.pk, spotName: spot.name });
        }
    }, [spot]);

    if (!spot || loadingSpots) return <Loader />;

    return (
        <RevenueProvider>
            <DashboardContent title="Revenue" actionItem={<DownloadReport filters={filters} spot={spot} />}>
                <MonthlyAndDailyRevenue spot={spot} filters={filters} urlUpdater={urlUpdater} />
                <div className={styles.widgetContainer}>
                    <div>
                        <RevenueByRentalType spot={spot} filters={filters} urlUpdater={urlUpdater} />
                        <Pacing spot={spot} />
                    </div>
                    <YoYRevenueWidget spot={spot} push={push} filters={filters} urlUpdater={urlUpdater} />
                </div>
            </DashboardContent>
        </RevenueProvider>
    );
}

function mapStateToProps(state: AppState) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        spot: selectedSpotInfo(state)!,
        loadingSpots: state.spots.orgSpotsLoading,
    };
}

const mapDispatchToProps = {
    push,
};

export default connect(mapStateToProps, mapDispatchToProps)(RevenueContainer);
